// Byte-canonical synthesized text templates (§3.6, §3.7): UTF-8, `\n` newlines, no trailing
// whitespace, keys in template order, filled only from named inputs. Changing any of these
// bytes changes root CIDs — they are normative vector artifacts (§C.3b).
//
// These are frozen reference templates for THIS implementation; the exact injection points and
// snippet wording are self-defined (not yet externally cross-validated).

import type { MaslIcon } from "../types.ts";

const utf8 = new TextEncoder();
export function toBytes(s: string): Uint8Array {
  return utf8.encode(s);
}

/** Escape a string for embedding in HTML attribute/text context. */
function htmlEscape(s: string): string {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

/** A resource key (always `/`-rooted, e.g. `/photo.png`) as a document-relative reference. */
function rel(p: string): string {
  return p.replace(/^\//, "");
}

/**
 * The block injected into the entry HTML `<head>` (§3.6): manifest link, theme-color and
 * apple-mobile-web-app-* meta, and the verifier service-worker registration snippet. Fixed
 * template; `\n`-joined; no trailing whitespace. `themeColor` omitted when unset. All three
 * URLs (manifest, apple-touch-icon, and the SW script) are DOCUMENT-RELATIVE (via {@link rel}) so
 * they resolve under both per-origin serving (§11, where they sit at the origin root) and the tile
 * subpath (§9.6, where the browser URL keeps the `/.well-known/szzt/tile/` prefix) — an absolute `/…` resolves against
 * the origin root and 404s (or, for the SW, would claim origin-wide scope) under the subpath. This is
 * the same class as the `rel()` fix applied to the synthesized-index emitters. A relative SW script URL
 * also scopes the worker to the served subtree rather than the whole origin.
 */
export function headInjection(opts: { name: string; themeColor?: string }): string {
  const lines = [
    `<link rel="manifest" href="${rel("/manifest.webmanifest")}">`,
    `<meta name="apple-mobile-web-app-capable" content="yes">`,
    `<meta name="apple-mobile-web-app-status-bar-style" content="default">`,
    `<meta name="apple-mobile-web-app-title" content="${htmlEscape(opts.name)}">`,
    `<link rel="apple-touch-icon" href="${rel("/icon-192.png")}">`,
  ];
  if (opts.themeColor !== undefined) {
    lines.push(`<meta name="theme-color" content="${htmlEscape(opts.themeColor)}">`);
  }
  lines.push(
    `<script>if("serviceWorker" in navigator){addEventListener("load",function(){navigator.serviceWorker.register("${rel("/sw.js")}")})}</script>`,
  );
  return lines.map((l) => "    " + l).join("\n");
}

/** Insert the head injection into an HTML document deterministically. */
export function injectIntoHtml(html: string, injection: string): string {
  // Skip if a manifest link already exists (author wins).
  if (/<link[^>]+rel=["']?manifest/i.test(html)) return html;
  const block = "\n" + injection + "\n";
  const headClose = html.search(/<\/head\s*>/i);
  if (headClose !== -1) return html.slice(0, headClose) + block + html.slice(headClose);
  const bodyClose = html.search(/<\/body\s*>/i);
  if (bodyClose !== -1) return html.slice(0, bodyClose) + block + html.slice(bodyClose);
  return html + block;
}

/** A synthesized index that links to a single non-HTML file (§14). */
export function singleFileIndex(name: string, target: string): string {
  return [
    `<!doctype html>`,
    `<html lang="en">`,
    `<head>`,
    `<meta charset="utf-8">`,
    `<meta name="viewport" content="width=device-width, initial-scale=1">`,
    `<title>${htmlEscape(name)}</title>`,
    `</head>`,
    `<body>`,
    `<main>`,
    `<h1>${htmlEscape(name)}</h1>`,
    `<p><a href="${htmlEscape(rel(target))}">${htmlEscape(rel(target))}</a></p>`,
    `</main>`,
    `</body>`,
    `</html>`,
    ``,
  ].join("\n");
}

/** A synthesized directory-listing index over the given paths (§14). Paths are sorted by caller. */
export function directoryIndex(name: string, paths: string[]): string {
  const items = paths.map((p) => `<li><a href="${htmlEscape(rel(p))}">${htmlEscape(rel(p))}</a></li>`);
  return [
    `<!doctype html>`,
    `<html lang="en">`,
    `<head>`,
    `<meta charset="utf-8">`,
    `<meta name="viewport" content="width=device-width, initial-scale=1">`,
    `<title>${htmlEscape(name)}</title>`,
    `</head>`,
    `<body>`,
    `<main>`,
    `<h1>${htmlEscape(name)}</h1>`,
    `<ul>`,
    ...items,
    `</ul>`,
    `</main>`,
    `</body>`,
    `</html>`,
    ``,
  ].join("\n");
}

/**
 * A synthesized image slideshow over the given image paths (§14). One image at a time, with
 * ←/→ keyboard navigation, prev/next buttons, and a counter; wraps around. Paths are sorted by
 * the caller and embedded as a JSON array (safe JS-string context). Byte-canonical: UTF-8,
 * `\n` newlines, no trailing whitespace — changing any byte forks the root CID (§3.7).
 */
export function imageSlideshow(name: string, paths: string[]): string {
  return [
    `<!doctype html>`,
    `<html lang="en">`,
    `<head>`,
    `<meta charset="utf-8">`,
    `<meta name="viewport" content="width=device-width, initial-scale=1">`,
    `<title>${htmlEscape(name)}</title>`,
    `<style>`,
    `*{margin:0;box-sizing:border-box}`,
    `html,body{height:100%}`,
    `body{background:#111;color:#eee;font-family:system-ui,sans-serif;display:flex;flex-direction:column}`,
    `main{flex:1;display:flex;align-items:center;justify-content:center;overflow:hidden}`,
    `img{max-width:100%;max-height:100%;object-fit:contain}`,
    `nav{display:flex;align-items:center;justify-content:center;gap:1rem;padding:.75rem;font-size:.9rem}`,
    `button{background:#333;color:#eee;border:0;border-radius:.25rem;padding:.4rem .9rem;font:inherit;cursor:pointer}`,
    `button:hover{background:#444}`,
    `#count{min-width:4rem;text-align:center}`,
    `</style>`,
    `</head>`,
    `<body>`,
    `<main><img id="img" alt=""></main>`,
    `<nav>`,
    `<button id="prev" aria-label="Previous image">&#8592;</button>`,
    `<span id="count"></span>`,
    `<button id="next" aria-label="Next image">&#8594;</button>`,
    `</nav>`,
    `<script>`,
    `var slides = ${JSON.stringify(paths.map(rel))};`,
    `var i = 0;`,
    `var img = document.getElementById("img");`,
    `var count = document.getElementById("count");`,
    `function show(){img.src = slides[i];img.alt = slides[i];count.textContent = (i + 1) + " / " + slides.length;}`,
    `function go(d){i = (i + d + slides.length) % slides.length;show();}`,
    `document.getElementById("prev").addEventListener("click", function(){go(-1);});`,
    `document.getElementById("next").addEventListener("click", function(){go(1);});`,
    `addEventListener("keydown", function(e){if(e.key === "ArrowLeft"){go(-1);}else if(e.key === "ArrowRight"){go(1);}});`,
    `show();`,
    `</script>`,
    `</body>`,
    `</html>`,
    ``,
  ].join("\n");
}

/** Neutralized author service worker (§3.6, §9.3): a no-op stub that cannot control the origin. */
export const NEUTRALIZED_SW = `// author service worker neutralized by the szzt builder (§9.3)\n`;

/**
 * The synthesized web app manifest (§3.6), `application/manifest+json`. Built as an object with
 * fixed key order and serialized with 2-space indent + trailing newline; JSON.stringify preserves
 * insertion order and emits no trailing whitespace, so the bytes are canonical.
 */
export function webManifest(opts: {
  name: string;
  short_name?: string;
  description?: string;
  theme_color?: string;
  background_color?: string;
  categories?: string[];
  icons: MaslIcon[];
}): string {
  const obj: Record<string, unknown> = { name: opts.name };
  if (opts.short_name !== undefined) obj["short_name"] = opts.short_name;
  if (opts.description !== undefined) obj["description"] = opts.description;
  // Document-relative (resolved against the manifest URL) so the PWA binds to THIS site under
  // both origin-root serving (§11) and the `/.well-known/szzt/tile/` subpath (§9.6); an absolute `/` would bind
  // start_url/scope to the origin root, not the served subpath.
  obj["start_url"] = "./";
  obj["scope"] = "./";
  obj["display"] = "standalone";
  if (opts.theme_color !== undefined) obj["theme_color"] = opts.theme_color;
  if (opts.background_color !== undefined) obj["background_color"] = opts.background_color;
  if (opts.categories !== undefined) obj["categories"] = opts.categories;
  obj["icons"] = opts.icons.map((ic) => {
    // `rel()` relativizes a `/`-rooted resource key (default icons) but leaves an absolute URL or an
    // already-relative author `src` untouched — resolved against the manifest URL under any mount.
    const o: Record<string, unknown> = { src: rel(ic.src) };
    if (ic.sizes !== undefined) o["sizes"] = ic.sizes;
    if (ic.type !== undefined) o["type"] = ic.type;
    if (ic.purpose !== undefined) o["purpose"] = ic.purpose;
    return o;
  });
  return JSON.stringify(obj, null, 2) + "\n";
}
